// ============================================================================
// Department Report — UI panel (ISOLATED MODULE)
// Rendered as a separate section under the existing per-user WorkSummaryGenerator.
// Admin-only. Does not import from or modify the existing generator.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Select, Button as AntButton, Modal, Progress, Checkbox } from 'antd';
import { AntRangePicker } from '../../../components/UI';
import { Building2, FileDown, FileSpreadsheet, AlertCircle, RotateCcw, Users } from 'lucide-react';
import moment from 'moment-timezone';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import useDepartmentReport from './useDepartmentReport';
import ReportView from './ReportView';
import { canUserAccessDeptReport } from './deptReportGating';
import { downloadDepartmentReportPDF, downloadDepartmentReportExcel } from './reportExport';

const TZ = 'Asia/Kolkata';

const DepartmentReportPanel = () => {
  const { user } = useAuth();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const isAdmin = canUserAccessDeptReport(user); // org-allowlisted + admin

  const {
    departments,
    membersByDept,
    loadingDepartments,
    loadDepartments,
    running,
    progress,
    report,
    error,
    generate,
    cancel,
  } = useDepartmentReport();

  const [department, setDepartment] = useState(undefined);
  const [selectedIds, setSelectedIds] = useState([]); // members to include (defaults to all)
  const [range, setRange] = useState({
    startDate: moment.tz(TZ).subtract(1, 'day').format('YYYY-MM-DD'),
    endDate: moment.tz(TZ).subtract(1, 'day').format('YYYY-MM-DD'),
  });

  // Load departments once, admins only.
  useEffect(() => {
    if (isAdmin) loadDepartments();
  }, [isAdmin, loadDepartments]);

  // When the department changes, select all its members by default.
  const deptMembers = department ? (membersByDept[department] || []) : [];
  useEffect(() => {
    setSelectedIds(deptMembers.map((m) => m.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department, membersByDept]);

  if (!isAdmin) return null;

  const memberCount = selectedIds.length;
  const toggleMember = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };
  const allChecked = deptMembers.length > 0 && selectedIds.length === deptMembers.length;
  const someChecked = selectedIds.length > 0 && !allChecked;

  const onRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setRange({
        startDate: moment.tz(dates[0], TZ).format('YYYY-MM-DD'),
        endDate: moment.tz(dates[1], TZ).format('YYYY-MM-DD'),
      });
    } else {
      setRange({ startDate: null, endDate: null });
    }
  };

  const handleGenerate = () => {
    if (!department) return toast.error('Select a department');
    if (!range.startDate || !range.endDate) return toast.error('Select a date range');
    if (!selectedIds.length) return toast.error('Select at least one member');

    Modal.confirm({
      title: 'Generate department report?',
      icon: <AlertCircle size={18} style={{ color: '#f59e0b' }} />,
      content: (
        <div>
          This will generate one consolidated report for <b>{memberCount}</b> of <b>{deptMembers.length}</b>{' '}
          member(s) of <b>{department}</b> in a single AI pass over their combined activity. Fast and uses one
          AI credit.
        </div>
      ),
      okText: `Generate (${memberCount})`,
      cancelText: 'Cancel',
      onOk: () => generate(department, range.startDate, range.endDate, selectedIds),
    });
  };

  const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div
      style={{
        marginTop: 32,
        border: '1px solid rgba(148,163,184,0.25)',
        borderRadius: 16,
        padding: 24,
        background: 'rgba(99,102,241,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Building2 className="h-5 w-5" style={{ color: '#6366f1' }} />
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Department Report</h2>
        <span
          style={{
            fontSize: 11, fontWeight: 600, color: '#6366f1',
            background: 'rgba(99,102,241,0.12)', padding: '2px 8px', borderRadius: 999,
          }}
        >
          Admin
        </span>
      </div>
      <p style={{ marginTop: 0, marginBottom: 18, fontSize: 13, opacity: 0.7 }}>
        Generate one consolidated daily-summary report for every member of a department.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            <Building2 className="h-4 w-4 inline mr-2" /> Select Department
          </label>
          <Select
            value={department}
            onChange={setDepartment}
            placeholder={loadingDepartments ? 'Loading departments…' : `Select department (${departments.length})`}
            loading={loadingDepartments}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            disabled={running}
            options={departments.map((d) => ({
              value: d,
              label: `${d} (${(membersByDept[d] || []).length})`,
            }))}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Select Date Range
          </label>
          <AntRangePicker
            value={range.startDate && range.endDate
              ? [moment.tz(range.startDate, TZ), moment.tz(range.endDate, TZ)]
              : null}
            onChange={onRangeChange}
            placeholder={['Start date', 'End date']}
            disabledDate={(current) => {
              // Disable today and any future date — reports only cover past days.
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return current && current.toDate() >= today;
            }}
            style={{ width: '100%' }}
            disabled={running}
          />
        </div>
      </div>

      {/* Member selection — all checked by default; uncheck to exclude */}
      {department && deptMembers.length > 0 && (
        <div
          style={{
            marginBottom: 16, padding: 14, borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.2)', background: 'rgba(148,163,184,0.06)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Users className="h-4 w-4" /> Members ({selectedIds.length}/{deptMembers.length} selected)
            </span>
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              disabled={running}
              onChange={(e) => setSelectedIds(e.target.checked ? deptMembers.map((m) => m.id) : [])}
            >
              Select all
            </Checkbox>
          </div>
          <div
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 6, maxHeight: 220, overflowY: 'auto',
            }}
          >
            {deptMembers.map((m) => (
              <Checkbox
                key={m.id}
                checked={selectedIds.includes(m.id)}
                disabled={running}
                onChange={(e) => toggleMember(m.id, e.target.checked)}
              >
                <span style={{ fontSize: 13 }}>
                  {m.name}
                  {m.position ? <span style={{ opacity: 0.55 }}> · {m.position}</span> : null}
                </span>
              </Checkbox>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <AntButton
          type="primary"
          onClick={handleGenerate}
          loading={running}
          disabled={!department || !range.startDate || selectedIds.length === 0}
        >
          {running ? 'Generating…' : `Generate Report${memberCount ? ` (${memberCount})` : ''}`}
        </AntButton>
        {running && (
          <AntButton danger icon={<RotateCcw className="h-4 w-4" />} onClick={cancel}>
            Cancel
          </AntButton>
        )}
        {report && !running && (
          <>
            <AntButton icon={<FileDown className="h-4 w-4" />} onClick={() => downloadDepartmentReportPDF(report)}>
              Download PDF
            </AntButton>
            <AntButton
              icon={<RotateCcw className="h-4 w-4" />}
              onClick={() => {
                const p = report._params || {};
                generate(p.department || department, p.startDate || range.startDate, p.endDate || range.endDate, p.selectedIds || selectedIds, true);
              }}
            >
              Regenerate
            </AntButton>
            {/* <AntButton icon={<FileSpreadsheet className="h-4 w-4" />} onClick={() => downloadDepartmentReportExcel(report)}>
              Download Excel
            </AntButton> */}
          </>
        )}
      </div>

      {running && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={pct} status="active" showInfo={false} />
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            {progress.label || 'Generating department report…'}
          </div>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 16, color: '#ef4444', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {report && !running && <ReportView report={report} isDark={isDark} />}
    </div>
  );
};

export default DepartmentReportPanel;
